import { Header } from './Header';
import { useChrome } from '@/arche/context/ChromeContext';
import { ExpandedArcheHeader } from '@/arche/components/ExpandedArcheHeader';

interface LayoutProps {
  children: React.ReactNode;
  /**
   * For a page that can be EXPANDED like an arche (focus mode — My Data, which
   * is the Vault Documents workspace): the arche id whose chrome state it
   * shares. While that arche is expanded the platform Header gives way to the
   * thin `ExpandedArcheHeader` (Back to Fias / Worktop / Fias AI / Collapse),
   * which is then the way back. Omitted = an ordinary page, always headed.
   */
  focusModeKey?: string;
  /**
   * The page renders the focus-mode controls itself (My Data puts them beside
   * its view tabs), so no thin bar while expanded — the same option
   * `ArcheLayout` has. A page that sets this MUST render the controls in every
   * state, or it can't be collapsed.
   */
  hideExpandedHeader?: boolean;
}

export function Layout({ children, focusModeKey, hideExpandedHeader = false }: LayoutProps) {
  const { isHeadersHidden } = useChrome();
  const expanded = !!focusModeKey && isHeadersHidden(focusModeKey);
  return (
    // The safe-area pads keep the header out from under the notch and the last
    // of a scrolled page off the home indicator, now that the viewport is
    // `viewport-fit=cover`. They are inside the `min-h-screen` box (Tailwind
    // makes everything border-box), so the page does not grow by them, and
    // they are zero on any screen without insets. See index.css.
    <div className="min-h-screen flex flex-col bg-linear-to-b from-background to-muted/30 pt-safe pb-safe px-safe">
      {!expanded ? (
        <Header />
      ) : (
        focusModeKey && !hideExpandedHeader && <ExpandedArcheHeader archeId={focusModeKey} />
      )}
      <main className="flex-1 flex flex-col">{children}</main>
    </div>
  );
}
